import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { Filter, QueryParameters, SortField } from '@app/core/entities/query-parameters.class';
import { UtilService } from '@app/core/services/utils/util.service';
import * as fromCarriers from '@app/pages/delivery-service/store/reducers';
import { FulfillService } from '@app/pages/workspace/fulfill/fulfill.service';
import { Carrier } from '@app/services/carriers/carrier.model';
import { Order } from '@app/services/order/order.model';
import { OrderService } from '@app/services/order/order.service';
import { ShopService } from '@app/services/shops/shop.service';
import { ColumnSettings, tableMessages } from '@app/sharedModule/models/table-settings.model';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { PdqToastComponent } from '@app/storybook/components/toast/toast.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { Store } from '@ngrx/store';
import { OrderStatus } from '@pdqdev/pdq-shared/dist/entities';
import moment from 'moment';

import { CookieService } from 'ngx-cookie-service';
import { catchError, filter, finalize, first } from 'rxjs/operators';
import {
  TABLES,
  TimeUnits,
  addDays,
  createStepFilter,
  getDeliveryPromise,
  modifyDateRangeValuesByColumn,
} from 'src/utils';
import { ordersToManifestColumnsConfig } from './orders-to-manifest-table.config';
import { CarrierTypeNames } from '@pdqdev/pdq-shared/dist/CarrierData';

@Component({
  selector: 'app-shipping-manifest-create',
  templateUrl: './shipping-manifest-create.component.html',
  styleUrls: ['./shipping-manifest-create.component.scss'],
  standalone: false,
})
export class ShippingManifestCreateComponent implements OnInit {
  @Input() batches = [];
  @Input() shopMetadata: any;
  @Output() completeCreate = new EventEmitter();
  carriers: DropdownOption[] = [];
  locations: DropdownOption[] = [];
  selectedCarrier;
  selectedLocation;
  filteredBatches;
  columns: ColumnSettings[] = [...ordersToManifestColumnsConfig];
  loading;
  focusedOrderId: string | null = null;
  totalCountOrders: number;
  ordersPerPage = 50;
  activePage = 1;
  totalPages = 1;
  allPages = {};
  temp = [];
  rows = [];
  selected = [];
  isBottomScrolled: boolean = false;
  pickupDate;
  readonly TABLES = TABLES;
  private sortFields: SortField[];
  private tableFilters: Filter[];
  startDateControl = new UntypedFormControl(new Date());
  endDateControl = new UntypedFormControl(new Date());
  isOpenDatePicker = false;
  startDate = new Date();
  startDateOneMonthAgo = new Date(new Date().setDate(new Date().getDate() - 30));
  endDate = new Date(new Date().setDate(new Date().getDate() + 4));
  loadingCreate = false;
  emptyMessage;

  private destroyRef = inject(DestroyRef);

  constructor(
    private orderService: OrderService,
    private cookieService: CookieService,
    public utilService: UtilService,
    private toastService: ToastService,
    private shopService: ShopService,
    private store: Store<fromCarriers.State>,
    private fulfillService: FulfillService,
  ) {
    this.ordersPerPage = orderService.getOrderPerPage();
  }

  ngOnInit(): void {
    this.setCarriersOptions();
    this.setLocationsOptions();
    this.startDateControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.getPagedOrders(0);
    });
    this.endDateControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.getPagedOrders(0));
    this.getPagedOrders(0);
    this.emptyMessage = `
    <div class="no-orders">
    <img src="/assets/icons/search-entire.svg" />
    <div class="text">No orders found to create a manifest.</div>
  </div>
    `;
  }

  setCarriersOptions() {
    if (this.fulfillService.usePaginatedFulfillment()) {
      this.setCarriersOptionsFromStore();
    } else {
      this.setCarriersOptionsFromBatches();
    }
  }

  private setCarriersOptionsFromStore(): void {
    this.store
      .select(fromCarriers.selectCarriers)
      .pipe(
        filter((carriers) => !!carriers?.length),
        first(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((carriers: Carrier[]) => {
        carriers
          .filter((carrier) => carrier.isActive)
          .forEach((carrier) => {
            const customFields = carrier.type?.custom_fields;
            const apiManifestSupport: string[] = customFields?.apiManifestSupport || [];
            const manifestSettings = customFields?.manifestSettings;

            // If carrier has providers that support manifest, create entries for each
            if (apiManifestSupport.length > 0) {
              apiManifestSupport.forEach((providerName) => {
                const label = `${carrier.name} - ${providerName}`;
                this.carriers.push({
                  label,
                  value: label,
                  type: providerName,
                  originalLabel: carrier.id.toString(),
                  hasExternalManifest: true,
                  supportPastDays: manifestSettings?.supportPastDays,
                  singleDay: manifestSettings?.singleDay,
                } as DropdownOption);
              });
            } else {
              // Carrier without external manifest support - create single entry
              const label = carrier.name;
              this.carriers.push({
                label,
                value: label,
                type: carrier.type?.name === CarrierTypeNames.PDQ_SHIPPING_P2PG ? 'P2PG' : null,
                originalLabel: carrier.id.toString(),
                hasExternalManifest: false,
                supportPastDays: manifestSettings?.supportPastDays,
                singleDay: manifestSettings?.singleDay,
              } as DropdownOption);
            }
          });

        this.selectedCarrier = this.carriers[0];
        this.setStartDate();
      });
  }

  private setCarriersOptionsFromBatches(): void {
    this.batches.forEach((batch) => {
      const label = `${batch.selectedCarrier.name}${batch.finalCarrier ? ' - ' + batch.finalCarrier : ''}`;
      const carrierExists = this.carriers.some((carrier) => carrier.label === label);
      if (!carrierExists) {
        const hasExternalManifest = batch.selectedCarrier?.type?.custom_fields?.apiManifestSupport?.includes(
          batch.finalCarrier,
        );
        const supportPastDays = batch.selectedCarrier?.type?.custom_fields?.manifestSettings?.supportPastDays;
        const singleDay = batch.selectedCarrier?.type?.custom_fields?.manifestSettings?.singleDay;
        this.carriers.push({
          label,
          value: label,
          type: this.getCarrierType(batch),
          originalLabel: batch.selectedCarrier.id,
          hasExternalManifest: hasExternalManifest,
          supportPastDays: supportPastDays,
          singleDay: singleDay,
        } as DropdownOption);
      }
    });
    this.selectedCarrier = this.carriers[0];
    this.setStartDate();
  }

  getCarrierType(batch: any) {
    return batch?.selectedCarrier?.type?.name === CarrierTypeNames.PDQ_SHIPPING_P2PG ? 'P2PG' : batch.finalCarrier;
  }

  setStartDate() {
    const startDateDaysAgo = this.shopMetadata?.batchExpirationDays ?? 30;
    this.startDate = this.selectedCarrier.hasExternalManifest
      ? new Date()
      : addDays(new Date(), -Number(startDateDaysAgo));
  }
  setLocationsOptions() {
    if (this.fulfillService.usePaginatedFulfillment()) {
      this.setLocationsOptionsFromShop();
    } else {
      this.setLocationsOptionsFromBatches();
    }
  }

  private setLocationsOptionsFromShop(): void {
    const shop = this.shopService.shopSignal();
    const shopLocations = shop?.shopLocations || [];

    shopLocations.forEach((location) => {
      const locationName = location.storeName;
      if (locationName) {
        this.locations.push({
          label: locationName,
          value: locationName,
        });
      }
    });

    this.selectedLocation = this.locations[0]?.value;
  }

  private setLocationsOptionsFromBatches(): void {
    this.batches.forEach((batch) => {
      const location = batch.shipFromAddress?.storeName;
      if (location && !location?.length) return;
      const locationExists = this.locations.filter((c) => c.value === location);
      if (!locationExists.length && location) {
        this.locations.push({
          label: location,
          value: location,
        });
      }
    });
    this.selectedLocation = this.locations[0]?.value;
  }

  carrierChanged(event) {
    this.selectedCarrier = event;
    this.setStartDate();
    this.getPagedOrders(0);
  }

  locationChanged(event) {
    this.selectedLocation = event.value;
    this.getPagedOrders(0);
  }

  getPagedOrders(page): void {
    if (!this.selectedLocation || !this.selectedCarrier) {
      this.loading = false;
      return;
    }
    this.loading = true;
    this.rows = [];
    this.selected = [];
    const queryParameters = this.sortFields?.length
      ? this.createQueryParameters(page, this.sortFields)
      : this.createQueryParameters(page);

    if (this.tableFilters?.length) {
      queryParameters.filters = [...queryParameters.filters, ...this.tableFilters];
    }

    this.orderService
      .getOrdersObs(queryParameters)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((data: { orders: Order[]; count: number }) => {
        this.processOrders(data);

        this.selected = [...data.orders];
      });
  }

  processOrders(data: { orders: Order[]; count: any } | any): void {
    if (data.count) {
      this.totalCountOrders = data?.count;
      this.totalPages = Math.ceil(data?.count / this.ordersPerPage);
    }
    this.utilService.setPages(this.activePage, this.allPages, this.totalPages);
    this.rows = data?.orders;
    this.rows?.forEach((row) => {
      row.batchName = row.orderBatch?.name ?? '';

      row.deliveryPromise = getDeliveryPromise(row);
    });
    this.temp = this.rows;
  }

  createQueryParameters(
    page: number,
    sortFields: SortField[] = [new SortField('createdDate', 'ASC')],
  ): QueryParameters {
    const queryParameters = new QueryParameters();
    queryParameters.page = page;
    queryParameters.maxResults = this.ordersPerPage;
    queryParameters.anyAll = 'all';
    const startDate = moment(this.startDateControl.value);
    const endDate = this.selectedCarrier.singleDay ? startDate : moment(this.endDateControl.value);
    const timeZone = this.selectedCarrier.hasExternalManifest ? 'America/Chicago' : this.shopMetadata.ianaTimeZone;
    const fromDateStartOfTheDay = moment.tz(`${startDate.format('YYYY-MM-DD')} 00:00:00`, timeZone);
    const toDateEndOfTheDay =
      endDate && endDate > startDate
        ? moment.tz(`${endDate.format('YYYY-MM-DD')} 23:59:59`, timeZone)
        : moment.tz(`${startDate.format('YYYY-MM-DD')} 23:59:59`, timeZone);

    /*
    Transfer to start of the day in Central Standard Time (shippo required for usps).
    This will need to be adjusted if we are going to separate it into internal and external manifests
    */

    queryParameters.filters = [
      new Filter('shipFromAddress.storeName', 'LIKE', [this.selectedLocation]),
      new Filter('orderBatch.pickupDate', 'IN', [
        fromDateStartOfTheDay.toDate().toISOString(),
        toDateEndOfTheDay.toDate().toISOString(),
      ]),
      new Filter('orderBatch.selectedCarrier.id', 'EQUAL', [this.selectedCarrier.originalLabel]),
      new Filter('shippingManifest', 'NOT IN', ['any value']),
    ];
    if (this.selectedCarrier.type) {
      queryParameters.filters.push(
        new Filter('selectedCarrierName', 'LIKE', [`deliveryTransaction:${this.selectedCarrier.type}`]),
      );
    }

    queryParameters.stepFilter = createStepFilter(OrderStatus.STEP_PRINT);
    queryParameters.sortFields = sortFields;
    return queryParameters;
  }

  onDrawerLoading(status: boolean): void {
    this.loading = status;
  }

  onChangeOrdersPerPage(ordersPerPage: number): void {
    this.ordersPerPage = ordersPerPage;
    this.cookieService.set('orderperpage_cookie', ordersPerPage.toString());
    this.activePage = 1;
    this.getPagedOrders(0);
  }

  paginate(page: number): void {
    this.activePage = page + 1;
    this.getPagedOrders(page);
  }

  jumpToPage(page: number): void {
    if (!page) {
      alert('Please enter a valid number');
      return;
    }

    if (page > this.totalPages) {
      alert(`Please enter a Page no. less than ${this.totalPages}`);
      return;
    }

    this.activePage = page;
    this.getPagedOrders(this.activePage - 1);
  }

  chooseEmptyMessage() {
    return tableMessages.NO_RESULT_MESSAGE;
  }

  sortColumns(sortOptions): void {
    this.columns = this.columns.map((column: ColumnSettings, i: number) => {
      if (i !== sortOptions.index) {
        return { ...column, sortDirection: null };
      }

      const sortDirection = sortOptions.sortDirection;
      const sortFieldNames: string[] = column.sortFieldName ? column.sortFieldName : [column.prop];

      this.sortFields = sortFieldNames.map((fieldName: string) => new SortField(fieldName, sortDirection));

      return {
        ...column,
        sortDirection,
      };
    });

    this.getPagedOrders(this.activePage - 1);
  }

  onChangeTableFilter(columnIndex: number): void {
    this.tableFilters = [];
    let fieldName;
    this.columns.forEach((column: ColumnSettings) => {
      if (!column.filterType || !column.filterOption || (!column.filterValue && !column.filterValueArray)) {
        return;
      }

      fieldName = column.filterFields ? column.filterFields : column.prop;
      let filterValue = [column.filterValue];

      if (column.filterOption === 'DATE RANGE' || column.filterOption === 'DATE RANGE STRICT') {
        filterValue = [];
        if (column.filterValueStart) {
          filterValue.push(column.filterValueStart);
        }
        if (column.filterValueEnd) {
          const endDate = new Date(Date.parse(column.filterValueEnd));
          endDate.setDate(endDate.getDate() + 1);
          modifyDateRangeValuesByColumn(column.prop, { endDate: endDate });
          filterValue.push(endDate.toISOString());
        }
      }

      if (column.prop === 'time_since_ordered') {
        filterValue = [];
        const date = new Date();
        // 2021-03-31T21:00:00.000Z
        if (column.filterValue === '> 5') {
          date.setDate(date.getDate() - 5);
          filterValue.push(moment(new Date(0)).format('YYYY-MM-DDTHH:mm:ss.SSS') + 'z');
          filterValue.push(moment(date).format('YYYY-MM-DDTHH:mm:ss.SSS') + 'z');
        } else {
          date.setDate(date.getDate() - Number(column.filterValue));
          filterValue.push(moment(date).format('YYYY-MM-DDTHH:mm:ss.SSS') + 'z');
          filterValue.push(moment(new Date()).format('YYYY-MM-DDTHH:mm:ss.SSS') + 'z');
        }
      }
      this.tableFilters.push(new Filter(fieldName, column.filterOption, filterValue));
    });

    this.activePage = 1;
    this.getPagedOrders(this.activePage - 1);
  }

  onBottomScrolled(onBottomScrolled: boolean): void {
    this.isBottomScrolled = onBottomScrolled;
  }

  onSelect(selectedOrders: Order[]): void {
    this.selected = [...selectedOrders];
  }

  createManifest() {
    if (this.selected.length) {
      this.loadingCreate = true;
      const date = new Date(this.startDateControl.value);
      const pickupDate = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 0),
      ).toISOString();
      const provider = this.selectedCarrier.label;
      this.orderService
        .createManifest(
          this.selected.map((item) => item.id),
          pickupDate,
          provider,
        )
        .pipe(
          catchError((error) => {
            this.toastService.show(PdqToastComponent, {
              message: 'Failed to create manifest',
              mode: 'danger',
              duration: TimeUnits._SECOND_IN_MS * 3,
            });
            this.completeCreate.emit();
            return error;
          }),
        )
        .subscribe((data) => {
          this.toastService.show(PdqToastComponent, {
            message: 'New manifest created',
            mode: 'success',
            duration: TimeUnits._SECOND_IN_MS * 3,
            icon: 'filled_v1',
          });
          this.completeCreate.emit();
        });
    }
  }
}
